(function() {
    if (window.SubscriptionServiceInitialized) {
        return;
    }
    window.SubscriptionServiceInitialized = true;

    const events = {
        subscriptionCheckboxUpdate: 'subscription:checkbox:update',
        subscriptionTabsUpdate: 'subscription:tabs:update',
        subscriptionTabsChange: 'subscription:tabs:change',
        subscriptionTabsToggle: 'subscription:tabs:toggle',
        subscriptionSelectUpdate: 'subscription:select:update',
        subscriptionSelectChangeOption: 'subscription:select:change-option',
        subscriptionRadioButtonsUpdate: 'subscription:radio-button:update',
        subscriptionUpdate: 'subscription:update',
        subscriptionSubmitButtonTextChange: 'subscription:submit-button-text:change',
        subscriptionPopupHide: 'subscription-popup:hide:trigger',
        productFormSubmit: 'product-form:submit',
        notificationModalShow: 'notification-modal:show',
        accountSubscriptionUpdate: 'account-subscription:update',
        subscriptionAccountSet: 'subscription:service:account:set',
        subscriptionAccountClear: 'subscription:service:account:clear',
    }

    class SubscriptionServiceState {
        constructor() {
            this.defaultStateInitialized = false;
            this.state = {};
            this.subscribers = new Set();
        }

        subscribe(callback) {
            this.subscribers.add(callback);
            
            return () => {
                this.subscribers.delete(callback);
            };
        }

        setState(newState) {
            const prevState = { ...this.state };
            this.state = { ...this.state, ...newState };
            this.subscribers.forEach(callback => {
                callback(this.state, prevState);
            });
            
            return { state: this.state, prevState };
        }

        getState() {
            return this.state;
        }

        initDefaultState(defaultState) {
            if (this.defaultStateInitialized) {
                return;
            }

            this.state = { ...this.state, ...defaultState };
            this.defaultStateInitialized = true;
        }
    }

    class SubscriptionStoreManager {
        constructor(widgetId) {
            this.widgetId = widgetId;
            if (SubscriptionStoreManager.instance) {
                return SubscriptionStoreManager.instance;
            }
            
            this.storeRegistry = new Map();
            SubscriptionStoreManager.instance = this;
        }

        createStore() {
            return new SubscriptionServiceState();
        }

        createStoreForWidget(widgetId) {
            if (!this.storeRegistry.has(widgetId)) {
                const store = this.createStore();
                this.storeRegistry.set(widgetId, store);
                
                store.subscribe((state) => {
                    document.dispatchEvent(new CustomEvent(events.subscriptionUpdate, {
                        detail: {
                            widgetId: widgetId,
                            ...state
                        }
                    }));
                });
            }
            return this.storeRegistry.get(widgetId);
        }

        getStoreForWidget(widgetId) {
            return this.storeRegistry.get(widgetId);
        }
    }
    
    class SubscriptionServiceManager {
        static classes = {
            loading: 'loading',
            disabled: 'disabled'
        };

        static subscriptionIdPropertyKey = '_subscription_id';
        static subscriptionUniqueIdPropertyKey = '_subscription_unique_id';
        static subscriptionBundleMainProductIdPropertyKey = '_subscription_bundle_main_sub_product_id';
        static subscriptionPrepaidPropertyValue = window?.cartStrings?.subscriptionPrepaidPropertyValue;

        static get subscriptionPrepaidPropertyMain() {
            return { "_is_prepaid_package": "true" };
        }

        static get subscriptionPrepaidPropertyInfo() {
            const property = {};
            const name = window?.cartStrings?.subscriptionPrepaidPropertyName;
            const value = SubscriptionServiceManager.subscriptionPrepaidPropertyValue;
            if (name && value) {
                property[name] = value;
            }
            return property;
        }

        static selectors = {
            subscriptionServiceProductSubmitButton: '.js-subscription-service-product-submit-button',
            subscriptionServiceSubmitButton: '.js-subscription-service-submit-button',
            subscriptionServiceSkipButton: '.js-subscription-service-skip-button'
        };

        constructor() {
            if (SubscriptionServiceManager.instance) {
                return SubscriptionServiceManager.instance;
            }
            SubscriptionServiceManager.instance = this;

            this.isRechargeBundleBf3MonthsTemplate = !!document?.querySelector("[data-recharge-bundle-bf-template]");
            this.isRechargeBundleBf1MonthTemplate = !!document?.querySelector("[data-recharge-bundle-bf-1-template]");
        }

        showNotificationModal(message) {
            window.dispatchEvent(new CustomEvent(events.notificationModalShow, {
                detail: {
                    message: message
                }
            }));
        }

        disableSubscriptionButtons(isLoading, targetButton) {
            const subscriptionProductSubmitButtons = document.querySelectorAll(SubscriptionServiceManager.selectors.subscriptionServiceProductSubmitButton);
            const subscriptionSubmitButtons = document.querySelectorAll(SubscriptionServiceManager.selectors.subscriptionServiceSubmitButton);
            const subscriptionSkipButtons = document.querySelectorAll(SubscriptionServiceManager.selectors.subscriptionServiceSkipButton);

            subscriptionProductSubmitButtons.forEach(button => {
                button.classList.toggle(SubscriptionServiceManager.classes.disabled, isLoading);
            });

            [...subscriptionSubmitButtons, ...subscriptionSkipButtons].forEach(button => {
                button.disabled = isLoading;
            });

            const targetProductSubmitButton = targetButton.classList.contains(SubscriptionServiceManager.selectors.subscriptionServiceProductSubmitButton.replace('.', ''));

            if (targetProductSubmitButton) {
                targetButton.classList.toggle(SubscriptionServiceManager.classes.loading, isLoading);
                targetButton.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !isLoading);
                targetButton.classList.toggle(SubscriptionServiceManager.classes.disabled, isLoading);
                isLoading ? targetButton.setAttribute('aria-disabled', true) : targetButton.removeAttribute('aria-disabled');
            } else {
                targetButton.classList.toggle(SubscriptionServiceManager.classes.loading, isLoading);
                targetButton.disabled = isLoading;
            }
        }

        async addToCart({ widgetId, form, targetButton, addAdditionalProduct, skipSubscription = false }) {
            if(!widgetId) return;

            const store = new SubscriptionStoreManager().getStoreForWidget(widgetId);

            if(!store || this.isLoading) return;

            this.isLoading = true;

            this.disableSubscriptionButtons(true, targetButton);

            const state = store.getState();
            let formData = [];
            let mainProductVariantId = null;
            let mainProductQuantity = null;
            let mainProductSellingPlan = null;
            const checkedUpsellItems = Array.from(document.querySelectorAll('.upsell-checkbox__checkbox:checked'));

            if (form) {
                formData = new FormData(form);
                mainProductVariantId = formData.get('id');
                mainProductQuantity = formData.get('quantity');
                mainProductSellingPlan = formData.get('selling_plan');
            }

            const properties = Object.fromEntries(
                Array.from(formData.entries())
                    .filter(([key]) => /properties\[.+\]/.test(key))
                    .map(([key, value]) => [key.match(/properties\[(.+)\]/)[1], value])
            );

            const subscriptionItem = (!this.isRechargeBundleBf1MonthTemplate && !this.isRechargeBundleBf3MonthsTemplate) || widgetId === "subscription-popup"
                ? this.getSubscriptionItem(state)
                // ? this.getSubscriptionItem(state, mainProductVariantId)
                : null;

            const mainProductItem = form ? this.getMainProductItem(mainProductVariantId, mainProductQuantity, properties, mainProductSellingPlan) : null;
            const selectedSubscriptionType = state.selectedSubscriptionType;
            const additionalProduct = addAdditionalProduct && state?.additionalProduct || {};
            const enableSubscription = skipSubscription ? false : state.enableSubscription;
            let isSubscriptionInCart = false;
            let isAdditionalProductInCart = false;
            let additionalProductItem;

            let items = [];
            let closeCartRequest = null;

            try {
                let cart = null;

                if (checkedUpsellItems.length > 0 || (selectedSubscriptionType === 'subscription' && enableSubscription)) {
                    cart = await this.getCart();
                    isSubscriptionInCart = this.hasSubscriptionInCart(cart);

                    const { variant_id, type, excludeVariantIds, preOrder } = additionalProduct;
                    additionalProductItem = this.getAdditionalProductItem(variant_id, type, preOrder);
                    isAdditionalProductInCart = this.hasProductInCart(cart, variant_id, ...(excludeVariantIds || []));
                }

                if (isSubscriptionInCart) {
                    this.showNotificationModal(window?.cartStrings?.alreadyIncludedSubscriptionError);
                    return;
                }

                if(mainProductItem && enableSubscription) {
                    items.push(mainProductItem, subscriptionItem);
                } else if (mainProductItem) {
                    items.push(mainProductItem);
                } else if (additionalProductItem && !isAdditionalProductInCart) {
                    items.push(additionalProductItem, subscriptionItem);
                } else {
                    items.push(subscriptionItem);
                }

                if (checkedUpsellItems.length > 0) {
                    for (const item of checkedUpsellItems) {
                        let upsellItem = {
                            id: Number(item.dataset.variantId),
                            quantity: 1
                        };

                        if (item.dataset.productType === 'Matt') {
                            upsellItem.properties = {"_is_product_matt": "true"};
                        }

                        if (item.dataset.productType === 'Lesson') {
                            upsellItem.properties = {"_lessons": "true", "_lessons_count": item.dataset.lessonsCount};
                        }

                        if (item.dataset.preOrder !== undefined) {
                            upsellItem.properties = {
                                ...(upsellItem.properties || {}),
                                "Stock status": "Pre-order"
                            };
                        }

                        const igItemProperty = window.igCustomData?.itemProperty;

                        if (igItemProperty) {
                            upsellItem.properties = {
                                ...(upsellItem.properties || {}),
                                ...igItemProperty
                            };
                        }

                        items.push(upsellItem);
                    }
                }
  
                this.cart = document.querySelector('cart-drawer');
                const sections = this.cart?.getSectionsToRender().map(section => section.id) || [];

                const payload = {
                    items: items,
                    sections,
                    sections_url: window.location.pathname
                };
                const cartMutationStamp = typeof markCartMutationStamp === 'function'
                    ? markCartMutationStamp()
                    : (window._CART_MUTATION_STAMP = (window._CART_MUTATION_STAMP || 0) + 1);
                let cartRequestClosed = false;
                closeCartRequest = () => {
                    if (cartRequestClosed) return;
                    cartRequestClosed = true;
                    if (typeof endCartRequest === 'function') {
                        endCartRequest(true);
                    } else {
                        window._CART_REQUESTS = Math.max(0, (window._CART_REQUESTS || 0) - 1);
                        if (!window._CART_REQUESTS) window._CART_ADD_IN_PROGRESS = false;
                    }
                };
                if (typeof beginCartRequest === 'function') {
                    beginCartRequest(true);
                } else {
                    window._CART_REQUESTS = (window._CART_REQUESTS || 0) + 1;
                    window._CART_ADD_IN_PROGRESS = true;
                }

                const response = await fetch(`${routes.cart_add_url}.js`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: JSON.stringify(payload)
                });

                const cartData = await response.json();
                cartData._cartMutationStamp = cartMutationStamp;
                closeCartRequest();

                if (cartData.status) {
                    this.showNotificationModal(cartData.description);
                    return;
                } else if (!this.cart) {
                    window.location = window.routes.cart_url;
                    return;
                }

                document.dispatchEvent(new CustomEvent(events.subscriptionPopupHide))
                const cartStateForSync = typeof this.cart.renderContentsWithFallback === 'function'
                    ? await this.cart.renderContentsWithFallback(cartData, undefined, cartMutationStamp)
                    : (this.cart.renderContents(cartData), cartData);
                if (typeof syncCartStateFromResponse === 'function') syncCartStateFromResponse(cartStateForSync);
            } catch (error) {
                if (closeCartRequest) closeCartRequest();
                this.showNotificationModal(error);
            } finally {
                this.isLoading = false;
                this.disableSubscriptionButtons(false, targetButton);
                if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                checkedUpsellItems.forEach(item => {
                    item.checked = false;
                })
            }
        }

        async addToCheckout({ widgetId, targetButton}) {
            if(!widgetId) return;

            const store = new SubscriptionStoreManager().getStoreForWidget(widgetId);

            if(!store || this.isLoading) return;

            this.isLoading = true;

            this.disableSubscriptionButtons(true, targetButton);

            const state = store.getState();

            const subscriptionItem = this.getSubscriptionItem(state);

            try {
                const responseClear = await fetch(`${routes.cart_clear_url}.js`,  {
                    method: 'POST',
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                });

                if(!responseClear.ok) {
                    throw new Error(`Request failed: ${responseClear?.statusText}`);
                }

                const response = await fetch(`${routes.cart_add_url}.js`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: JSON.stringify({items: [subscriptionItem]})
                });

                const cartData = await response.json();

                if (cartData.status) {
                    throw new Error(cartData.description);
                }

                window.location.assign('/checkout');
            } catch (error) {
                this.showNotificationModal(error);
            } finally {
                this.isLoading = false;
                this.disableSubscriptionButtons(false, targetButton);
            }
        }

        getMainProductItem(mainProductVariantId, mainProductQuantity, properties, sellingPlan) {
            if (this.isRechargeBundleBf1MonthTemplate || this.isRechargeBundleBf3MonthsTemplate) {

                properties = {
                    ...properties,
                    [SubscriptionServiceManager.subscriptionPrepaidPropertyValue]: this.isRechargeBundleBf1MonthTemplate ? "1 Month Plan Included" : this.isRechargeBundleBf3MonthsTemplate ? "3 Months Plan Included" : ""
                }
            }

            const item = {
                id: mainProductVariantId,
                quantity: mainProductQuantity || 1,
                properties: properties || {}
            };
            if (sellingPlan) {
                item.selling_plan = sellingPlan;
            }
            return item;
        }

        getAdditionalProductItem(variantId, type, preOrder) {
            if (!variantId) return null;

            const item = {
                id: variantId,
                quantity: 1
            };

            if(type === 'Matt') {
                item.properties = {"_is_product_matt": "true"};
            }

            if (preOrder) {
                item.properties = {
                    ...(item.properties || {}),
                    "Stock status": "Pre-order"
                };
            }

            const igItemProperty = window.igCustomData?.itemProperty;

            if (igItemProperty) {
                item.properties = {
                    ...(item.properties || {}),
                    ...igItemProperty
                };
            }

            return item;
        }

        getSubscriptionItem({ selectedVariantId, selectedSellingPlanId, selectedSubscriptionType }, mainProductVariantId = null) {
            const item = {
                id: selectedVariantId,
                selling_plan: selectedSellingPlanId,
                quantity: 1
            };

            item.properties = {
                [SubscriptionServiceManager.subscriptionIdPropertyKey]: selectedSellingPlanId,
                [SubscriptionServiceManager.subscriptionUniqueIdPropertyKey]: Date.now()
            };

            if (selectedSubscriptionType === 'prepaid') {
                item.properties = { 
                    ...item.properties,
                    ...SubscriptionServiceManager.subscriptionPrepaidPropertyMain, 
                    ...SubscriptionServiceManager.subscriptionPrepaidPropertyInfo,
                    ...(mainProductVariantId && {
                        [SubscriptionServiceManager.subscriptionBundleMainProductIdPropertyKey]: mainProductVariantId
                    })
                };
            }

            const igItemProperty = window.igCustomData?.itemProperty;

            if (igItemProperty) {
                item.properties = {
                    ...(item.properties || {}),
                    ...igItemProperty
                };
            }

            return item;
        }

        async getCart() {
            if (window._cartState && Array.isArray(window._cartState.items)) return window._cartState;
            if (typeof fetchCartJson === 'function') {
                const cart = await fetchCartJson();
                if (cart) return cart;
            }
            throw new Error('Cart state is unavailable');
        }


        hasSubscriptionInCart(cart) {
            return cart.items.some(item => {
                const isSubscriptionProduct = item.product_type === window.cartStrings.subscriptionProductType;
                const isSellingPlanAllocation = item.selling_plan_allocation;
                const [propertyKey, propertyValue] = Object.entries(SubscriptionServiceManager.subscriptionPrepaidPropertyMain)[0];
                const isPrepaidPackage = item.properties && item.properties[propertyKey] === propertyValue;
                return isSubscriptionProduct && isSellingPlanAllocation && !isPrepaidPackage;
            });
        }

        hasProductInCart(cart, ...variantIds) {
            if(!variantIds.length) return false;
            const normalizedIds = variantIds.map(Number);
            return cart.items.some(item => normalizedIds.includes(item.variant_id));
        }
    }

    window.SubscriptionServiceManager = window.SubscriptionServiceManager || SubscriptionServiceManager;

    class SubscriptionServiceCheckbox {
        static selectors = {
            checkboxInput: '.js-subscription-service-checkbox-input'
        };

        constructor(element) {
            this.element = element;
            this.input = null;
            this.initialized = false;
            this.dispatchCheckboxUpdate = this.dispatchCheckboxUpdate.bind(this);
            this.init();
        }

        init() {
            if(!this.element) return;

            this.input = this.element.querySelector(SubscriptionServiceCheckbox.selectors.checkboxInput);
            if (!this.input) return;

            this.initEventListeners();
            this.input.disabled = false;
            this.initialized = true;
        }

        initEventListeners() {
            this.input.addEventListener('change', this.dispatchCheckboxUpdate);
        }

        removeEventListeners() {
            this.input.removeEventListener('change', this.dispatchCheckboxUpdate);
        }

        destroy() {
            this.removeEventListeners();
        }

        changeCheckbox(checked) {
            if (this.input.checked === checked) {
                return;
            }

            this.input.checked = checked;
            this.dispatchCheckboxUpdate();
        }

        dispatchCheckboxUpdate() {
            this.element.dispatchEvent(
                new CustomEvent(events.subscriptionCheckboxUpdate, {
                    detail: {
                        checked: this.input.checked
                    },
                    bubbles: true
                })
            );
        }
    }

    class SubscriptionServiceTabs {
        static selectors = {
            tab: '.js-subscription-service-tab',
            scrollContainer: '.js-subscription-service-tabs-scroll-container'
        };

        static classes = {
            active: 'is-active'
        };

        constructor(element) {
            this.element = element;
            this.tabs = [];
            this.type = null;
            this.selectedTabValue = null;
            this.scrollContainer = null;
            this.changeTab = this.changeTab.bind(this);
            this.handleSubscriptionTabClick = this.handleSubscriptionTabClick.bind(this);
            this.initialized = false;
            this.init();
        }

        init() {
            if(!this.element) return;

            this.type = this.element.dataset.type;
            this.scrollContainer = this.element.querySelector(SubscriptionServiceTabs.selectors.scrollContainer);
            this.tabs = [...this.element.querySelectorAll(SubscriptionServiceTabs.selectors.tab)];
            
            if(!this.tabs.length || !this.scrollContainer) return;

            const activeTab = this.tabs.find(tab => tab.classList.contains(SubscriptionServiceTabs.classes.active));
            this.scrollToSelectedTab(activeTab);
            this.selectedTabValue = activeTab?.dataset.value;

            this.initEventListeners();

            this.initialized = true;
        }

        destroy() {
            this.removeEventListeners();
        }

        initEventListeners() {
            this.tabs.forEach(tab => {
                tab.addEventListener('click', this.handleSubscriptionTabClick);
            });

            if(this.type === 'subscription-type-tabs') {
                document.addEventListener(events.subscriptionTabsChange, (event) => this.changeTab(event.detail.value));
            }
        }

        removeEventListeners() {
            this.tabs.forEach(tab => {
                tab.removeEventListener('click', this.handleSubscriptionTabClick);
            });
        }

        handleSubscriptionTabClick(event) {
            const tab = event.target.closest(SubscriptionServiceTabs.selectors.tab);
            const value = tab.dataset.value;
            this.changeTab(value);
        }

        changeTab(value) {
            if (this.selectedTabValue === value) {
                return;
            }

            const activeTab = this.tabs.find(tab => tab.dataset.value === this.selectedTabValue)
            activeTab && activeTab.classList.remove(SubscriptionServiceTabs.classes.active);
            const tab = this.tabs.find(tab => tab.dataset.value === value);
            tab && tab.classList.add(SubscriptionServiceTabs.classes.active);
            
            this.selectedTabValue = value;

            this.scrollToSelectedTab(tab);
            this.dispatchTabsUpdate();
        }
        
        scrollToSelectedTab(selectedTab) {
            if (!this.scrollContainer || !selectedTab) {
                return;
            }

            const tabLeft = selectedTab.offsetLeft;
            const tabWidth = selectedTab.offsetWidth;
            const containerWidth = this.scrollContainer.offsetWidth;
            const scrollLeft = tabLeft - (containerWidth - tabWidth) / 2;
            this.scrollContainer.scrollTo({
                left: Math.max(0, scrollLeft),
                behavior: 'smooth'
            });
        }
        
        dispatchTabsUpdate() {
            this.element.dispatchEvent(
                new CustomEvent(events.subscriptionTabsUpdate, {
                    detail: {
                        value: this.selectedTabValue
                    },
                    bubbles: true
                })
            );
        }
    }

    class SubscriptionServiceSelect {
        static selectors = {
            toggle: '.js-subscription-service-select-toggle',
            toggleIconImage: '.js-subscription-service-select-toggle-icon-image',
            toggleTitle: '.js-subscription-service-select-toggle-title',
            dropdown: '.js-subscription-service-select-dropdown',
            scrollContainer: '.js-subscription-service-select-scroll-container',
            option: '.js-subscription-service-select-option'
        };

        static classes = {
            open: 'is-open',
            active: 'is-active',
            hidden: 'is-hidden'
        };

        static variables = {
            dropdownMaxHeight: '--subscription-service-select-dropdown-max-height'
        };

        constructor(element) {
            this.element = element;
            this.initialized = false;
            this.toggle = null;
            this.toggleIconImage = null;
            this.toggleTitle = null;
            this.dropdown = null;
            this.scrollContainer = null;
            this.options = [];
            this.type = null;
            this.selectedProductId = null;
            this.selectedVariantId = null;
            this.selectedSellingPlanId = null;
            this.disabled = false;
            this.handleDocumentClick = this.handleDocumentClick.bind(this);
            this.handleToggleClick = this.handleToggleClick.bind(this);
            this.handleOptionClick = this.handleOptionClick.bind(this);
            this.handleWindowResize = this.handleWindowResize.bind(this);
            this.setActiveStates = this.setActiveStates.bind(this);
            this.changeOption = this.changeOption.bind(this);
            this.init();
        }

        destroy() {
            this.removeEventListeners();
        }

        init() {
            if(!this.element) return;

            this.type = this.element.dataset.type;
            this.toggle = this.element.querySelector(SubscriptionServiceSelect.selectors.toggle);
            this.toggleIconImage = this.element.querySelector(SubscriptionServiceSelect.selectors.toggleIconImage);
            this.toggleTitle = this.element.querySelector(SubscriptionServiceSelect.selectors.toggleTitle);
            this.dropdown = this.element.querySelector(SubscriptionServiceSelect.selectors.dropdown);
            this.scrollContainer = this.element.querySelector(SubscriptionServiceSelect.selectors.scrollContainer);
            this.options = [...this.element.querySelectorAll(SubscriptionServiceSelect.selectors.option)];
            
            if(!this.toggle || !this.toggleTitle || !this.dropdown || !this.options.length || !this.scrollContainer) return;

            this.setActiveStates();
            this.initEventListeners();
            this.initialized = true;
        }

        setActiveStates() {
            this.selectedProductId = this.options.find(option => option.classList.contains(SubscriptionServiceSelect.classes.active))?.dataset.productId;
            this.selectedVariantId = this.options.find(option => option.classList.contains(SubscriptionServiceSelect.classes.active))?.dataset.variantId;
            this.selectedSellingPlanId = this.options.find(option => option.classList.contains(SubscriptionServiceSelect.classes.active))?.dataset.sellingPlanId;
        }

        initEventListeners() {
            this.toggle.addEventListener('click', this.handleToggleClick);
            this.options.forEach(option => {
                option.addEventListener('click', this.handleOptionClick);
            });
            document.addEventListener('click', this.handleDocumentClick);
            window.addEventListener('resize', this.handleWindowResize);
            document.addEventListener(events.subscriptionSelectChangeOption, (event) => this.changeOption(event.detail));
        }

        removeEventListeners() {
            this.toggle.removeEventListener('click', this.handleToggleClick);
            this.options.forEach(option => {
                option.removeEventListener('click', this.handleOptionClick);
            });
            document.removeEventListener('click', this.handleDocumentClick);
            window.removeEventListener('resize', this.handleWindowResize);
            document.removeEventListener(events.subscriptionSelectChangeOption, (event) => this.changeOption(event.detail));
        }

        handleToggleClick() {
            this.toggleDropdown();
        }

        handleOptionClick(event) {
            const option = event.target.closest(SubscriptionServiceSelect.selectors.option);
            const {productId, variantId, sellingPlanId} = option.dataset;

            this.changeOption({productId, variantId, sellingPlanId});
        }

        handleDocumentClick(event) {
            if (!this.element.contains(event.target)) {
                this.closeDropdown();
            }
        }

        handleWindowResize() {
            // Recalculate max height if dropdown is open
            if (this.element.classList.contains(SubscriptionServiceSelect.classes.open)) {
                this.setDropdownMaxHeight();
            }
        }

        changeOption(payload) {
            const {productId, variantId, sellingPlanId} = payload;

            if (this.selectedProductId === productId && this.selectedVariantId === variantId && this.selectedSellingPlanId === sellingPlanId) {
                return;
            }

            const activeOption = this.options.find(option =>
                option.dataset.productId === this.selectedProductId
                && option.dataset.variantId === this.selectedVariantId
                && option.dataset.sellingPlanId === this.selectedSellingPlanId
            );

            activeOption.classList.remove(SubscriptionServiceSelect.classes.active);
            activeOption.disabled = false;

            const option = this.options.find(option =>
                option.dataset.productId === productId
                && option.dataset.variantId === variantId
                && option.dataset.sellingPlanId === sellingPlanId
            );

            const title = option.dataset.title;
            const icon = option.dataset.icon;
            
            this.toggleTitle.innerHTML = title;

            if (this.toggleIconImage) {
                if (icon) {
                    this.toggleIconImage.classList.remove(SubscriptionServiceSelect.classes.hidden);
                    this.toggleIconImage.src = icon;
                } else {
                    this.toggleIconImage.classList.add(SubscriptionServiceSelect.classes.hidden);
                }
            }

            this.selectedProductId = productId;
            this.selectedVariantId = variantId;
            this.selectedSellingPlanId = sellingPlanId;
            option.classList.add(SubscriptionServiceSelect.classes.active);
            option.disabled = true;
            
            this.closeDropdown();
            this.dispatchSelectUpdate();
        }

        getState() {
            return {
                selectedProductId: this.selectedProductId,
                selectedVariantId: this.selectedVariantId,
                selectedSellingPlanId: this.selectedSellingPlanId
            };
        }

        changeType(type) {
            if (this.type === type) {
                this.element.classList.add(SubscriptionServiceSelect.classes.active);
            } else {
                this.element.classList.remove(SubscriptionServiceSelect.classes.active);
            }
        }

        toggleDisabled(disabled) {
            this.disabled = disabled;
            this.toggle.disabled = this.disabled;
            this.disabled && this.closeDropdown();
        }

        toggleDropdown() {
            this.element.classList.contains(SubscriptionServiceSelect.classes.open) ? this.closeDropdown() : this.openDropdown();
        }
        
        openDropdown() {
            this.element.classList.add(SubscriptionServiceSelect.classes.open);
            this.scrollContainer.scrollTop = 0;
            this.setDropdownMaxHeight();
        }

        closeDropdown() {
            this.element.classList.remove(SubscriptionServiceSelect.classes.open);
            this.scrollContainer.scrollTop = 0;
        }

        setDropdownMaxHeight() {
            if (!this.dropdown) return;

            const subscriptionService = this.element.closest('subscription-service');
            if (!subscriptionService) return;

            const dropdownRect = this.dropdown.getBoundingClientRect();
            const serviceRect = subscriptionService.getBoundingClientRect();
            const availableSpace = serviceRect.bottom - dropdownRect.top;            
            const maxHeight = Math.max(80, availableSpace);
            
            this.dropdown.style.setProperty(SubscriptionServiceSelect.variables.dropdownMaxHeight, `${maxHeight}px`);
        }

        dispatchSelectUpdate() {
            this.element.dispatchEvent(
                new CustomEvent(events.subscriptionSelectUpdate, {
                    detail: {
                        productId: this.selectedProductId,
                        variantId: this.selectedVariantId,
                        sellingPlanId: this.selectedSellingPlanId
                    },
                    bubbles: true
                })
            );
        }
    }

    class SubscriptionServiceRadioButtons {
        static selectors = {
            radioButton: ".js-subscription-service-radio-button"
        }

        static classes = {
            active: 'is-active'
        };

        constructor(element) {
            this.element = element;
            this.initialized = false;
            this.selectedProductId = null;
            this.selectedVariantId = null;
            this.selectedSellingPlanId = null;
            this.radioButtons = [];
            this.handleRadioButtonClick = this.handleRadioButtonClick.bind(this);
            this.init();
        }

        destroy() {
            this.removeEventListeners();
        }

        init() {
            if(!this.element) return;
            this.type = this.element.dataset.type;
            this.radioButtons = [...this.element.querySelectorAll(SubscriptionServiceRadioButtons.selectors.radioButton)];

            if(!this.radioButtons.length) return;

            this.setActiveStates();
            this.initEventListeners();
            this.initialized = true;
        }

        setActiveStates() {
            const activeRadioButton = this.radioButtons.find(radioButton => radioButton.classList.contains(SubscriptionServiceRadioButtons.classes.active));
            const { productId, variantId, sellingPlanId } = activeRadioButton.dataset;
            this.selectedProductId = productId;
            this.selectedVariantId = variantId;
            this.selectedSellingPlanId = sellingPlanId;
        }

        initEventListeners() {
            this.radioButtons.forEach(radioButton => radioButton.addEventListener('click', this.handleRadioButtonClick))
        }

        removeEventListeners() {
            this.radioButtons.forEach(radioButton => radioButton.removeEventListener('click', this.handleRadioButtonClick))
        }

        handleRadioButtonClick(event) {
            const radioButton = event.target.closest(SubscriptionServiceRadioButtons.selectors.radioButton);
            const {productId, variantId, sellingPlanId} = radioButton.dataset;
            this.changeRadioButton({productId, variantId, sellingPlanId});
        }

        changeRadioButton(payload) {
            const {productId, variantId, sellingPlanId} = payload;

            if (this.selectedProductId === productId && this.selectedVariantId === variantId && this.selectedSellingPlanId === sellingPlanId) {
                return;
            }

            const activeRadioButton = this.radioButtons.find(radioButton =>
                radioButton.dataset.productId === this.selectedProductId
                && radioButton.dataset.variantId === this.selectedVariantId
                && radioButton.dataset.sellingPlanId === this.selectedSellingPlanId
            );

            activeRadioButton.classList.remove(SubscriptionServiceRadioButtons.classes.active);
            activeRadioButton.disabled = false;

            const radioButton = this.radioButtons.find(radioButton =>
                radioButton.dataset.productId === productId
                && radioButton.dataset.variantId === variantId
                && radioButton.dataset.sellingPlanId === sellingPlanId
            );

            this.selectedProductId = productId;
            this.selectedVariantId = variantId;
            this.selectedSellingPlanId = sellingPlanId;
            radioButton.classList.add(SubscriptionServiceRadioButtons.classes.active);
            radioButton.disabled = true;

            this.dispatchRadioButtonUpdate();
        }

        getState() {
            return {
                selectedProductId: this.selectedProductId,
                selectedVariantId: this.selectedVariantId,
                selectedSellingPlanId: this.selectedSellingPlanId
            };
        }

        changeType(type) {
            if (this.type === type) {
                this.element.classList.add(SubscriptionServiceRadioButtons.classes.active);
            } else {
                this.element.classList.remove(SubscriptionServiceRadioButtons.classes.active);
            }
        }

        dispatchRadioButtonUpdate() {
            this.element.dispatchEvent(
                new CustomEvent(events.subscriptionRadioButtonsUpdate, {
                    detail: {
                        productId: this.selectedProductId,
                        variantId: this.selectedVariantId,
                        sellingPlanId: this.selectedSellingPlanId
                    },
                    bubbles: true
                })
            );
        }
    }

    class SubscriptionServiceContent {
        static selectors = {
            tab: '.js-subscription-service-content-tab'
        };

        static classes = {
            active: 'is-active',
            disabled: 'is-disabled'
        };

        constructor(element) {
            this.element = element;
            this.selectedContentTabValue = null;
            this.initialized = false;
            this.init();
        }

        init() {
            if(!this.element) return;

            this.tabs = [...this.element.querySelectorAll(SubscriptionServiceContent.selectors.tab)];
            if(!this.tabs.length) return;

            this.selectedContentTabValue = this.tabs.find(tab => tab.classList.contains(SubscriptionServiceContent.classes.active))?.dataset.value;
            this.initialized = true;
        }

        changeTab(value) {
            if (this.selectedContentTabValue === value) {
                return;
            }

            const activeTab = this.tabs.find(tab => tab.dataset.value === this.selectedContentTabValue)
            activeTab && activeTab.classList.remove(SubscriptionServiceContent.classes.active);
            const tab = this.tabs.find(tab => tab.dataset.value === value);
            tab && tab.classList.add(SubscriptionServiceContent.classes.active);
            this.selectedContentTabValue = value;
        }

        disableContent() {
            this.element.classList.toggle(SubscriptionServiceContent.classes.disabled);
        }
    }

    if (!customElements.get('subscription-service')) {
        class SubscriptionService extends HTMLElement {
            static classes = {
                active: 'is-active',
                hidden: 'is-hidden',
                disabled: 'is-disabled'
            };

            static selectors = {
                state: '.js-subscription-service-state',
                subscriptionCheckbox: '.js-subscription-service-checkbox',
                subscriptionTabs: '.js-subscription-service-tabs',
                subscriptionSelect: '.js-subscription-service-select',
                subscriptionContent: '.js-subscription-service-content',
                subscriptionTypeContent: '.js-subscription-service-type-content',
                subscriptionTitleSelected: '.js-subscription-service-title-selected',
                subscriptionNote: '.js-subscription-service-note',
                subscriptionTrialPlanTileElement: '.js-subscription-service-trial-plan-tile-element',
                subscriptionChangeButton: '.js-subscription-service-change-button',
                subscriptionSubmitButton: '.js-subscription-service-submit-button',
                subscriptionSubmitButtonText: '.js-subscription-service-submit-button-text',
                subscriptionSkipButton: '.js-subscription-service-skip-button',
                parentSubscriptionPopup: 'subscription-popup',
                cartDrawer: 'cart-drawer'
            };

            static attrs = {
                cartDrawerPopup: 'cart-drawer-popup'
            };

            static mediaQuery = {
                mobile: '(max-width: 991px)'
            };

            constructor() {
                super();
                this.widgetId = null;
                this.store = null;
                this.subscriptionServiceManager = null;
                this.subscriptionCheckbox = null;
                this.subscriptionTabsWrapper = null;
                this.subscriptionTabs = null;
                this.subscriptionSelects = null;
                this.subscriptionContent = null;
                this.subscriptionTypeContent = null;
                this.subscriptionTitleSelected = null;
                this.subscriptionNotes = null;
                this.subscriptionTrialPlanTileElement = null;
                this.subscriptionChangeButton = null;
                this.subscriptionSubmitButton = null;
                this.subscriptionSubmitButtonText = null;
                this.subscriptionSkipButton = null;
                this.defaultState = null;
                this.mobileMediaQuery = null;
                this.parentSubscriptionPopup = null;
                this.accountSubscriptionStore = null;
                this.handleMobileMediaQueryChange = this.handleMobileMediaQueryChange.bind(this);
                this.handleSubscriptionCheckboxUpdate = this.handleSubscriptionCheckboxUpdate.bind(this);
                this.handleSubscriptionTabsUpdate = this.handleSubscriptionTabsUpdate.bind(this);
                this.handleSubscriptionSelectUpdate = this.handleSubscriptionSelectUpdate.bind(this);
                this.handleSubscriptionSubmitButton = this.handleSubscriptionSubmitButton.bind(this);
                this.handleSubscriptionSubmitButtonText = this.handleSubscriptionSubmitButtonText.bind(this);
                this.handleSubscriptionSkipButton = this.handleSubscriptionSkipButton.bind(this);
                this.handleSubscriptionTabsToggle = this.handleSubscriptionTabsToggle.bind(this);
                this.setSubscriptionServiceAccountData = this.setSubscriptionServiceAccountData.bind(this);
                this.clearSubscriptionServiceAccountData = this.clearSubscriptionServiceAccountData.bind(this);
            }

            connectedCallback() {
                this.init();
            }

            disconnectedCallback() {
                this.cleanup();
            }

            init() {
                this.widgetId = this.dataset.widgetId;
                this.store = new SubscriptionStoreManager().createStoreForWidget(this.widgetId);
                this.subscriptionServiceManager = new SubscriptionServiceManager();
                this.initDefaultState();
                this.initComponents();
                this.initEventListeners();
                this.changeStateByScreen();
                this.updateComponents(this.store.getState(), this.defaultState);
            }

            removeEventListeners() {
                this.mobileMediaQuery.removeEventListener('change', this.handleMobileMediaQueryChange);
                this.removeEventListener(events.subscriptionCheckboxUpdate, this.handleSubscriptionCheckboxUpdate);
                this.removeEventListener(events.subscriptionTabsUpdate, this.handleSubscriptionTabsUpdate);
                this.removeEventListener(events.subscriptionSelectUpdate, this.handleSubscriptionSelectUpdate);
                this.subscriptionSubmitButton && this.subscriptionSubmitButton.removeEventListener('click', this.handleSubscriptionSubmitButton);
                this.subscriptionSkipButton && this.subscriptionSkipButton.removeEventListener('click', this.handleSubscriptionSkipButton);
                this.unsubscribe && this.unsubscribe();
                document.removeEventListener(events.subscriptionSubmitButtonTextChange, this.handleSubscriptionSubmitButtonText);
                document.removeEventListener(events.subscriptionTabsToggle, this.handleSubscriptionTabsToggle);
                document.removeEventListener(events.subscriptionAccountSet, (event) => this.setSubscriptionServiceAccountData(event));
                document.removeEventListener(events.subscriptionAccountClear, this.clearSubscriptionServiceAccountData);
            }

            cleanup() {
                this.destroyComponents();
                this.removeEventListeners();
            }

            initEventListeners() {
                this.mobileMediaQuery = window.matchMedia(SubscriptionService.mediaQuery.mobile);
                this.mobileMediaQuery.addEventListener('change', this.handleMobileMediaQueryChange);
                this.addEventListener(events.subscriptionCheckboxUpdate, this.handleSubscriptionCheckboxUpdate);
                this.addEventListener(events.subscriptionTabsUpdate, this.handleSubscriptionTabsUpdate);
                this.addEventListener(events.subscriptionSelectUpdate, this.handleSubscriptionSelectUpdate);
                this.subscriptionSubmitButton && this.subscriptionSubmitButton.addEventListener('click', this.handleSubscriptionSubmitButton);
                this.subscriptionSkipButton && this.subscriptionSkipButton.addEventListener('click', this.handleSubscriptionSkipButton);
                this.unsubscribe = this.store.subscribe((state, prevState) => this.onStateChange(state, prevState));
                document.addEventListener(events.subscriptionSubmitButtonTextChange, this.handleSubscriptionSubmitButtonText);
                document.addEventListener(events.subscriptionTabsToggle, this.handleSubscriptionTabsToggle);
                document.addEventListener(events.subscriptionAccountSet, (event) => this.setSubscriptionServiceAccountData(event));
                document.addEventListener(events.subscriptionAccountClear, this.clearSubscriptionServiceAccountData);
            }

            clearSubscriptionServiceAccountData() {
                this.accountSubscriptionStore = null;

                if (this.subscriptionSubmitButton) this.subscriptionSubmitButton.disabled = false;
            }

            setSubscriptionServiceAccountData(event) {
                const {productId, variantId, sellingPlanId} = event.detail;

                this.accountSubscriptionStore = {
                    productId,
                    variantId,
                    sellingPlanId
                };

                this.updateComponents(this.store.getState(), this.defaultState);
            }

            handleSubscriptionTabsToggle(event) {
                const {isHidden} = event.detail;

                if (!this.subscriptionTabsWrapper) return null;

                this.subscriptionTabsWrapper.classList.toggle(SubscriptionService.classes.hidden, isHidden);
            }

            handleSubscriptionSubmitButtonText(event) {
                const {value} = event.detail;

                if (!value || !this.subscriptionSubmitButtonText) return null;

                this.subscriptionSubmitButtonText.textContent = value;
            }

            handleMobileMediaQueryChange() {
                this.changeProductPopupByScreen();
                this.changeStateByScreen();
            }

            changeProductPopupByScreen() {
                const isMobile = this.mobileMediaQuery.matches;
                if (!isMobile) {
                    document.dispatchEvent(new CustomEvent(events.subscriptionPopupHide, { detail: { type: 'product-popup' } }));
                }
            }

            changeStateByScreen() {
                const isMobile = this.mobileMediaQuery.matches;
                const enableSubscriptionDesktop = this?.dataset?.enableSubscriptionDesktop;
                const enableSubscriptionMobile = this?.dataset?.enableSubscriptionMobile;

                if(enableSubscriptionDesktop === undefined || enableSubscriptionMobile === undefined) {
                    return;
                }

                if(isMobile) {
                    this.store.setState({ enableSubscription:enableSubscriptionMobile === 'true' });
                } else {
                    this.store.setState({ enableSubscription:enableSubscriptionDesktop === 'true' });
                }
            }

            updateStateByScreen(state, prevState) {
                const enableSubscriptionDesktop = this?.dataset?.enableSubscriptionDesktop;
                const enableSubscriptionMobile = this?.dataset?.enableSubscriptionMobile;

                if(enableSubscriptionDesktop === undefined || enableSubscriptionMobile === undefined) {
                    return;
                }

                const { enableSubscription } = state;
                const { enableSubscription: prevEnableSubscription } = prevState;
                
                if(enableSubscription === prevEnableSubscription) {
                    return;
                }

                const isMobile = this.mobileMediaQuery.matches;

                if(isMobile) {
                    this.dataset.enableSubscriptionMobile = enableSubscription;
                } else {
                    this.dataset.enableSubscriptionDesktop = enableSubscription;
                }
            }
            
            onStateChange(state, prevState) {
               this.updateStateByScreen(state, prevState);
               this.updateComponents(state, prevState);
            }

            initDefaultState() {
                const stateScript = this.querySelector(SubscriptionService.selectors.state);
                if (!stateScript) return;
                
                try {
                    this.defaultState = JSON.parse(stateScript.textContent);
                } catch (e) {
                    console.error('Failed to parse subscription state JSON:', e);
                }

                if (this.store.defaultStateInitialized) return;

                this.store.initDefaultState(this.defaultState);
            }

            initComponent(selector, ComponentClass, multiple = false) {
                if (multiple) {
                    const elements = this.querySelectorAll(selector);
                    return elements.length > 0 ? Array.from(elements).map(el => {
                        const instance = new ComponentClass(el);
                        return instance && instance.initialized ? instance : null;
                    }) : [];
                } else {
                    const element = this.querySelector(selector);
                    const instance = element ? new ComponentClass(element) : null;
                    return instance && instance.initialized ? instance : null;
                }
            }

            initComponents() {
                this.subscriptionCheckbox = this.initComponent(SubscriptionService.selectors.subscriptionCheckbox, SubscriptionServiceCheckbox, false);
                this.subscriptionTabs = this.initComponent(SubscriptionService.selectors.subscriptionTabs, SubscriptionServiceTabs, false);
                this.subscriptionSelects = this.initComponent(SubscriptionService.selectors.subscriptionSelect, SubscriptionServiceSelect, true);
                this.subscriptionContent = this.initComponent(SubscriptionService.selectors.subscriptionContent, SubscriptionServiceContent, false);
                this.subscriptionTypeContent = this.initComponent(SubscriptionService.selectors.subscriptionTypeContent, SubscriptionServiceContent, false);
                this.parentSubscriptionPopup = this.closest(SubscriptionService.selectors.parentSubscriptionPopup);
                this.subscriptionTabsWrapper = this.querySelector(SubscriptionService.selectors.subscriptionTabs);
                this.subscriptionTitleSelected = this.querySelector(SubscriptionService.selectors.subscriptionTitleSelected);
                this.subscriptionNotes = this.querySelectorAll(SubscriptionService.selectors.subscriptionNote);
                this.subscriptionTrialPlanTileElement = this.querySelector(SubscriptionService.selectors.subscriptionTrialPlanTileElement);
                this.subscriptionChangeButton = this.querySelector(SubscriptionService.selectors.subscriptionChangeButton);
                this.subscriptionSubmitButton = this.querySelector(SubscriptionService.selectors.subscriptionSubmitButton);
                this.subscriptionSubmitButtonText = this.querySelector(SubscriptionService.selectors.subscriptionSubmitButtonText);
                this.subscriptionSkipButton = this.querySelector(SubscriptionService.selectors.subscriptionSkipButton);
            }

            destroyComponents() {
                this.subscriptionCheckbox && this.subscriptionCheckbox.destroy();
                this.subscriptionTabs && this.subscriptionTabs.destroy();
                this.subscriptionSelects && this.subscriptionSelects.length && this.subscriptionSelects.forEach(subscriptionSelect => subscriptionSelect.destroy());
            }

            updateComponents(state, prevState) {
                this.subscriptionCheckbox && this.updateSubscriptionCheckbox(state, prevState);
                this.subscriptionTabs && this.updateSubscriptionTabs(state, prevState);
                this.subscriptionSelects.length && this.updateSubscriptionSelects(state, prevState);
                this.subscriptionContent && this.updateSubscriptionContent(state, prevState);
                this.subscriptionTypeContent && this.updateSubscriptionTypeContent(state, prevState);
                this.subscriptionTitleSelected && this.updateSubscriptionTitleSelected(state, prevState);
                this.subscriptionNotes.length && this.updateSubscriptionNotes(state, prevState);
                this.subscriptionTrialPlanTileElement && this.updateSubscriptionTrialPlanTileElement(state, prevState);
                this.subscriptionChangeButton && this.updateSubscriptionChangeButton(state, prevState);
                this.updateSubscriptionSubmitButton(state, prevState);
                this.subscriptionSkipButton && this.updateSubscriptionSkipButton(state, prevState);
            }

            handleSubscriptionCheckboxUpdate(event) {
                event.stopPropagation();

                const { checked } = event.detail;
                const { enableSubscription } = this.store.getState();

                if (checked === enableSubscription) {
                    return;
                }

                this.store.setState({enableSubscription: checked});
            }

            handleSubscriptionTabsUpdate(event) {
                event.stopPropagation();

                const { value } = event.detail;
                const { selectedSubscriptionType } = this.store.getState();

                if (selectedSubscriptionType === value) {
                    return;
                }

                const currentSelect = this.subscriptionSelects && this.subscriptionSelects.length ? this.subscriptionSelects.find(subscriptionSelect => subscriptionSelect.type === value) : null;
                
                let newState = { selectedSubscriptionType: value };
                
                if (currentSelect) {
                    const selectState = currentSelect.getState();
                    newState = {...newState, ...selectState};
                }

                this.store.setState(newState);
            }

            handleSubscriptionSelectUpdate(event) {
                event.stopPropagation();

                const { productId, variantId, sellingPlanId } = event.detail;
                const { selectedProductId, selectedVariantId, selectedSellingPlanId } = this.store.getState();

                if (selectedProductId === productId &&
                    selectedVariantId === variantId &&
                    selectedSellingPlanId === sellingPlanId
                ) return;

                this.store.setState({
                    selectedProductId: productId,
                    selectedVariantId: variantId,
                    selectedSellingPlanId: sellingPlanId
                });
            }

            updateSubscriptionTabs(state, prevState) {
                const {selectedSubscriptionType} = state;
                const {selectedSubscriptionType: prevSelectedSubscriptionType} = prevState;

                if (selectedSubscriptionType !== prevSelectedSubscriptionType) {
                    this.subscriptionTabs && this.subscriptionTabs.changeTab(selectedSubscriptionType);
                }
            }

            updateSubscriptionSelects(state, prevState) {
                const {enableSubscription, selectedSubscriptionType, selectedProductId, selectedVariantId, selectedSellingPlanId} = state;
                const {enableSubscription: prevEnableSubscription, selectedSubscriptionType: prevSelectedSubscriptionType, selectedProductId: prevSelectedProductId, selectedVariantId: prevSelectedVariantId, selectedSellingPlanId: prevSelectedSellingPlanId} = prevState;
               
                if (selectedSubscriptionType !== prevSelectedSubscriptionType) {
                    this.subscriptionSelects.forEach(subscriptionSelect => {
                        subscriptionSelect.changeType(selectedSubscriptionType);
                    });
                }

                if (selectedProductId !== prevSelectedProductId || selectedVariantId !== prevSelectedVariantId || selectedSellingPlanId !== prevSelectedSellingPlanId) {
                    const currentSelect = this.subscriptionSelects.find(subscriptionSelect => subscriptionSelect.type === selectedSubscriptionType);
                    currentSelect && currentSelect.changeOption({productId: selectedProductId, variantId: selectedVariantId, sellingPlanId: selectedSellingPlanId});
                }

                if (enableSubscription !== prevEnableSubscription) {
                    this.subscriptionSelects.forEach(subscriptionSelect => {
                        subscriptionSelect.toggleDisabled(!enableSubscription);
                    });
                }
            }

            updateSubscriptionCheckbox(state, prevState) {
                const {enableSubscription} = state;
                const {enableSubscription: prevEnableSubscription} = prevState;

                if (enableSubscription !== prevEnableSubscription) {
                    this.subscriptionCheckbox && this.subscriptionCheckbox.changeCheckbox(enableSubscription);
                }
            }

            updateSubscriptionContent(state, prevState) {
                const {enableSubscription, selectedProductId} = state;
                const {enableSubscription: prevEnableSubscription, selectedProductId: prevSelectedProductId} = prevState;

                if (selectedProductId !== prevSelectedProductId || enableSubscription !== prevEnableSubscription) {
                    this.subscriptionContent.changeTab(enableSubscription === false ? '' : selectedProductId.toString());
                }
            }

            updateSubscriptionTypeContent(state, prevState) {
                const {enableSubscription, selectedSubscriptionType} = state;
                const {enableSubscription: prevEnableSubscription, selectedSubscriptionType: prevSelectedSubscriptionType} = prevState;
                
                if (selectedSubscriptionType !== prevSelectedSubscriptionType) {
                    this.subscriptionTypeContent.changeTab(selectedSubscriptionType);
                }

                if (enableSubscription !== prevEnableSubscription) {
                    this.subscriptionTypeContent.disableContent(enableSubscription);
                }
            }

            getSelectedProductPlan(state) {
                const product = state.products.find(product => product.id === state.selectedProductId);
                const variant = product.variants.find(variant => variant.id === state.selectedVariantId);
                const sellingPlan = variant.sellingPlanAllocations.find(sellingPlan => sellingPlan.id === state.selectedSellingPlanId);
                return sellingPlan;
            }

            updateSubscriptionTitleSelected(state, prevState) {
                const {enableSubscription, selectedProductId, selectedVariantId, selectedSellingPlanId, selectedSubscriptionType} = state;
                const {enableSubscription: prevEnableSubscription, selectedVariantId: prevSelectedVariantId, selectedProductId: prevSelectedProductId, selectedSubscriptionType: prevSelectedSubscriptionType, selectedSellingPlanId: prevSelectedSellingPlanId} = prevState;

                if (selectedProductId !== prevSelectedProductId || selectedVariantId !== prevSelectedVariantId || enableSubscription !== prevEnableSubscription || selectedSubscriptionType !== prevSelectedSubscriptionType || selectedSellingPlanId !== prevSelectedSellingPlanId) {
                    const sellingPlan = this.getSelectedProductPlan(state);

                    if(!sellingPlan) return;

                    const isTrial = sellingPlan?.isTrial;
                    const type = isTrial ? 'trial' : selectedSubscriptionType;
                    this.subscriptionTitleSelected.innerHTML = sellingPlan.title[type] || '';

                    const changeActiveState = this.subscriptionTitleSelected.dataset.toggleActive !== undefined;
                    changeActiveState && this.subscriptionTitleSelected.classList.toggle(SubscriptionService.classes.active, enableSubscription);
                }
            }

            updateSubscriptionNotes(state, prevState) {
                const {selectedProductId, selectedVariantId, selectedSellingPlanId, selectedSubscriptionType} = state;
                const {selectedProductId: prevSelectedProductId, selectedVariantId: prevSelectedVariantId, selectedSellingPlanId: prevSelectedSellingPlanId,  selectedSubscriptionType: prevSelectedSubscriptionType} = prevState;

                if (selectedProductId !== prevSelectedProductId || selectedVariantId !== prevSelectedVariantId || selectedSellingPlanId !== prevSelectedSellingPlanId || selectedSubscriptionType !== prevSelectedSubscriptionType) {
                    const sellingPlan = this.getSelectedProductPlan(state);

                    if(!sellingPlan) return;

                    const isTrial = sellingPlan?.isTrial;

                    const noteType = isTrial ? 'trial' : selectedSubscriptionType;

                    this.subscriptionNotes.forEach(subscriptionNote => {
                        subscriptionNote.innerText = state?.noteText[noteType] || '';
                    });
                }
            }

            updateSubscriptionTrialPlanTileElement(state, prevState) {
                const { enableSubscription, selectedSubscriptionType } = state;
                const { enableSubscription: prevEnableSubscription, selectedSubscriptionType: prevSelectedSubscriptionType } = prevState;

                if (selectedSubscriptionType !== prevSelectedSubscriptionType) {
                    const isTrial = selectedSubscriptionType === 'subscription';
                    this.subscriptionTrialPlanTileElement.classList.toggle(SubscriptionService.classes.active, isTrial);
                }

                if (enableSubscription !== prevEnableSubscription) {
                    this.subscriptionTrialPlanTileElement.classList.toggle(SubscriptionService.classes.disabled, !enableSubscription);
                }
            }

            updateSubscriptionChangeButton(state, prevState) {
                const {enableSubscription} = state;
                const {enableSubscription: prevEnableSubscription} = prevState;

                if (enableSubscription !== prevEnableSubscription) {
                    this.subscriptionChangeButton.disabled = !enableSubscription;
                }
            }

            updateSubscriptionSubmitButton(state, prevState) {
                if (!this.subscriptionSubmitButton) return;

                const {selectedProductId, selectedVariantId, selectedSellingPlanId, enableSubscription} = state;
                const {enableSubscription: prevEnableSubscription} = prevState;

                if (!this.accountSubscriptionStore) {
                    if (enableSubscription !== prevEnableSubscription) {
                        this.subscriptionSubmitButton.disabled = !enableSubscription;
                    }

                    return;
                }

                const {productId, variantId, sellingPlanId} = this.accountSubscriptionStore;

                this.subscriptionSubmitButton.disabled = selectedProductId === productId && selectedVariantId === variantId && selectedSellingPlanId === sellingPlanId;
            }

            updateSubscriptionSkipButton(state, prevState) {
                const {enableSubscription} = state;
                const {enableSubscription: prevEnableSubscription} = prevState;
                
                if (enableSubscription !== prevEnableSubscription) {
                    this.subscriptionSkipButton.disabled = !enableSubscription;
                }
            }

            async handleSubscriptionSubmitButton() {
                if (this.parentSubscriptionPopup && this.parentSubscriptionPopup?.dataset?.type === 'update-subscription-plan-popup') {
                    const { selectedProductId, selectedVariantId, selectedSellingPlanId } = this.store.getState();

                    document.dispatchEvent(new CustomEvent(events.accountSubscriptionUpdate, {
                        detail: {
                            submitButton: this.subscriptionSubmitButton,
                            selectedProductId,
                            selectedVariantId,
                            selectedSellingPlanId
                        }
                    }));

                    return;
                }

                const isCartDrawerPopup = this.parentSubscriptionPopup && this.parentSubscriptionPopup?.dataset?.type === SubscriptionService.attrs.cartDrawerPopup;

                const formId = this.subscriptionSubmitButton.dataset.productFormId;
                const form = formId ? document.getElementById(formId) : null;
                
                if (formId && !form) return;

                await this.subscriptionServiceManager.addToCart({ 
                    widgetId: this.widgetId,
                    form: form,
                    addAdditionalProduct: isCartDrawerPopup,
                    targetButton: this.subscriptionSubmitButton
                });
            }

            async handleSubscriptionSkipButton() {
                const formId = this.subscriptionSkipButton.dataset.productFormId;
                const form = formId ? document.getElementById(formId) : null;
                
                if (formId && !form) return;

                await this.subscriptionServiceManager.addToCart({ 
                    widgetId: this.widgetId,
                    form: form,
                    targetButton: this.subscriptionSkipButton,
                    skipSubscription: true
                });
            }
        }

        customElements.define('subscription-service', SubscriptionService);
    }

    if (!customElements.get('subscription-service-split')) {
        class SubscriptionServiceSplit extends HTMLElement {
            static selectors = {
                state: '.js-subscription-service-state',
                subscriptionTabs: '.js-subscription-service-tabs',
                subscriptionRadioButtons: '.js-subscription-service-radio-buttons',
                subscriptionContent: '.js-subscription-service-content',
                subscriptionSubmitButton: '.js-subscription-service-submit-button'
            }

            constructor() {
                super();
                this.widgetId = null;
                this.store = null;
                this.subscriptionServiceManager = null;
                this.subscriptionTabs = null;
                this.subscriptionRadioButtons = null;
                this.subscriptionSubmitButton = null;
                this.subscriptionContent = null;
                this.defaultState = null;
                this.handleSubscriptionTabsUpdate = this.handleSubscriptionTabsUpdate.bind(this);
                this.handleSubscriptionRadioButtonsUpdate = this.handleSubscriptionRadioButtonsUpdate.bind(this);
                this.handleSubscriptionSubmitButton = this.handleSubscriptionSubmitButton.bind(this);
            }

            connectedCallback() {
                this.init();
            }

            disconnectedCallback() {
                this.cleanup();
            }

            init() {
                this.widgetId = this.dataset.widgetId;
                this.store = new SubscriptionStoreManager().createStoreForWidget(this.widgetId);
                this.subscriptionServiceManager = new SubscriptionServiceManager();
                this.initDefaultState();
                this.initComponents();
                this.initEventListeners();
                this.updateComponents(this.store.getState(), this.defaultState);
            }

            cleanup() {
                this.destroyComponents();
                this.removeEventListeners();
            }

            initEventListeners() {
                this.addEventListener(events.subscriptionTabsUpdate, this.handleSubscriptionTabsUpdate);
                this.addEventListener(events.subscriptionRadioButtonsUpdate, this.handleSubscriptionRadioButtonsUpdate);
                this.subscriptionSubmitButton && this.subscriptionSubmitButton.addEventListener('click', this.handleSubscriptionSubmitButton);
                this.unsubscribe = this.store.subscribe((state, prevState) => this.onStateChange(state, prevState));
            }

            removeEventListeners() {
                this.removeEventListener(events.subscriptionTabsUpdate, this.handleSubscriptionTabsUpdate);
                this.removeEventListener(events.subscriptionRadioButtonsUpdate, this.handleSubscriptionRadioButtonsUpdate);
                this.subscriptionSubmitButton && this.subscriptionSubmitButton.removeEventListener('click', this.handleSubscriptionSubmitButton);
                this.unsubscribe && this.unsubscribe();
            }

            onStateChange(state, prevState) {
                this.updateComponents(state, prevState);
            }

            initDefaultState() {
                const stateScript = this.querySelector(SubscriptionServiceSplit.selectors.state);
                if (!stateScript) return;

                try {
                    this.defaultState = JSON.parse(stateScript.textContent);
                } catch (e) {
                    console.error('Failed to parse subscription state JSON:', e);
                }

                if (this.store.defaultStateInitialized) return;

                this.store.initDefaultState(this.defaultState);
            }

            initComponent(selector, ComponentClass, multiple = false) {
                if (multiple) {
                    const elements = this.querySelectorAll(selector);
                    return elements.length > 0 ? Array.from(elements).map(el => {
                        const instance = new ComponentClass(el);
                        return instance && instance.initialized ? instance : null;
                    }) : [];
                } else {
                    const element = this.querySelector(selector);
                    const instance = element ? new ComponentClass(element) : null;
                    return instance && instance.initialized ? instance : null;
                }
            }

            initComponents() {
                this.subscriptionTabs = this.initComponent(SubscriptionServiceSplit.selectors.subscriptionTabs, SubscriptionServiceTabs, false);
                this.subscriptionRadioButtons = this.initComponent(SubscriptionServiceSplit.selectors.subscriptionRadioButtons, SubscriptionServiceRadioButtons, true);
                this.subscriptionContent = this.initComponent(SubscriptionServiceSplit.selectors.subscriptionContent, SubscriptionServiceContent, false);
                this.subscriptionSubmitButton = this.querySelector(SubscriptionServiceSplit.selectors.subscriptionSubmitButton);
            }

            destroyComponents() {
                this.subscriptionTabs && this.subscriptionTabs.destroy();
                this.subscriptionRadioButtons && this.subscriptionRadioButtons.length && this.subscriptionRadioButtons.forEach(subscriptionRadioButton => subscriptionRadioButton.destroy());
            }

            updateComponents(state, prevState) {
                this.subscriptionTabs && this.updateSubscriptionTabs(state, prevState);
                this.subscriptionRadioButtons.length && this.updateSubscriptionRadioButtons(state, prevState);
                this.subscriptionContent && this.updateSubscriptionContent(state, prevState);
            }

            handleSubscriptionTabsUpdate(event) {
                event.stopPropagation();

                const { value } = event.detail;
                const { selectedProductId } = this.store.getState();

                if (selectedProductId === value) {
                    return;
                }

                let newState = { selectedProductId: value };

                const currentRadioButtons = this.subscriptionRadioButtons.find(subscriptionRadioButton => subscriptionRadioButton.type === value);
                if (currentRadioButtons) {
                    const radioButtonState = currentRadioButtons.getState();
                    newState = {...newState, ...radioButtonState};
                }

                this.store.setState(newState);
            }

            handleSubscriptionRadioButtonsUpdate(event) {
                event.stopPropagation();

                const { productId, variantId, sellingPlanId } = event.detail;
                const { selectedProductId, selectedVariantId, selectedSellingPlanId } = this.store.getState();

                if (selectedProductId === productId && selectedVariantId === variantId && selectedSellingPlanId === sellingPlanId) return;

                this.store.setState({
                    selectedProductId: productId,
                    selectedVariantId: variantId,
                    selectedSellingPlanId: sellingPlanId
                });
            }

            updateSubscriptionTabs(state, prevState) {
                const { selectedProductId } = state;
                const { selectedProductId: prevSelectedProductId } = prevState;

                if (selectedProductId !== prevSelectedProductId) {
                    this.subscriptionTabs && this.subscriptionTabs.changeTab(selectedProductId);
                }
            }

            updateSubscriptionRadioButtons(state, prevState) {
                const {selectedProductId, selectedVariantId, selectedSellingPlanId} = state;
                const {selectedProductId: prevSelectedProductId, selectedVariantId: prevSelectedVariantId, selectedSellingPlanId: prevSelectedSellingPlanId} = prevState;

                if (selectedProductId !== prevSelectedProductId) {
                    this.subscriptionRadioButtons.forEach(subscriptionRadioButton => {
                        subscriptionRadioButton.changeType(selectedProductId);
                    });

                    return;
                }

                if (selectedProductId !== prevSelectedProductId || selectedVariantId !== prevSelectedVariantId || selectedSellingPlanId !== prevSelectedSellingPlanId) {
                    const currentRadioButtons = this.subscriptionRadioButtons.find(subscriptionRadioButton => subscriptionRadioButton.type === prevSelectedProductId);
                    currentRadioButtons && currentRadioButtons.changeRadioButton({productId: selectedProductId, variantId: selectedVariantId, sellingPlanId: selectedSellingPlanId});
                }
            }

            async handleSubscriptionSubmitButton() {
                const { isDirectCheckout } = this.store.getState();
                const options = {
                    widgetId: this.widgetId,
                    targetButton: this.subscriptionSubmitButton
                };

                if (isDirectCheckout) {
                    await this.subscriptionServiceManager.addToCheckout(options);
                } else {
                    await this.subscriptionServiceManager.addToCart(options);
                }
            }

            updateSubscriptionContent(state, prevState) {
                const {enableSubscription, selectedProductId} = state;
                const {enableSubscription: prevEnableSubscription, selectedProductId: prevSelectedProductId} = prevState;

                if (selectedProductId !== prevSelectedProductId || enableSubscription !== prevEnableSubscription) {
                    this.subscriptionContent.changeTab(enableSubscription === false ? '' : selectedProductId.toString());
                }
            }
        }

        customElements.define('subscription-service-split', SubscriptionServiceSplit);
    }

    if(!customElements.get('subscription-service-selected-title')) {
        class SubscriptionServiceSelectedTitle extends HTMLElement {
            constructor() {
                super();
                this.widgetId = null;
                this.handleDispatchUpdate = this.handleDispatchUpdate.bind(this);
            }

            connectedCallback() {
                this.init();
            }

            disconnectedCallback() {
                this.cleanup();
            }

            init() {
                this.widgetId = this.dataset.widgetId;
                this.initEventListeners();
            }

            cleanup() {
                this.removeEventListeners();
            }

            initEventListeners() {
                document.addEventListener(events.subscriptionUpdate, this.handleDispatchUpdate);
            }

            removeEventListeners() {
                window.removeEventListener(events.subscriptionUpdate, this.handleDispatchUpdate);
            }

            handleDispatchUpdate(event) {
                const { widgetId } = event.detail;

                if (widgetId !== this.widgetId) {
                    return;
                }

                const { selectedProductId,  selectedVariantId, selectedSellingPlanId, selectedSubscriptionType, products } = event.detail;
                const product = products.find(product => product.id === selectedProductId);
                const variant = product.variants.find(variant => variant.id === selectedVariantId);
                const sellingPlan = variant.sellingPlanAllocations.find(sellingPlan => sellingPlan.id === selectedSellingPlanId);
                const sellingPlanTitle = sellingPlan.title[selectedSubscriptionType];

                if(!sellingPlanTitle) return;

                this.innerText = sellingPlanTitle;
            }
        }

        customElements.define('subscription-service-selected-title', SubscriptionServiceSelectedTitle);
    }

    if (!customElements.get('subscription-service-price')) {
        class SubscriptionServicePrice extends HTMLElement {
            static classes = {
                active: 'is-active'
            };

            static selectors = {
                priceValue: ".js-subscription-price-value"
            };

            constructor() {
                super();
                this.widgetId = null;
                this.priceValue = null;
                this.handleDispatchPriceUpdate = this.handleDispatchPriceUpdate.bind(this);
            }

            connectedCallback() {
                this.init();
            }

            disconnectedCallback() {
                this.cleanup();
            }
            
            init() {
                this.widgetId = this.dataset.widgetId;
                this.productPrice = this.dataset.productPrice ? parseInt(this.dataset.productPrice) : null;

                this.initEventListeners();
            }

            cleanup() {
                this.removeEventListeners();
            }
    
            initEventListeners() {
                document.addEventListener(events.subscriptionUpdate, this.handleDispatchPriceUpdate);
            }
    
            removeEventListeners() {
                window.removeEventListener(events.subscriptionUpdate, this.handleDispatchPriceUpdate);
            }
    
            handleDispatchPriceUpdate(event) {
                const { widgetId } = event.detail;
                if (widgetId !== this.widgetId) {
                    return;
                } 

                const { enableSubscription, selectedProductId, selectedVariantId, selectedSellingPlanId, selectedSubscriptionType, products } = event.detail;
                const product = products.find(product => product.id === selectedProductId);
                const variant = product.variants.find(variant => variant.id === selectedVariantId);
                const sellingPlan = variant.sellingPlanAllocations.find(sellingPlan => sellingPlan.id === selectedSellingPlanId);
                const sellingPlanPrice = sellingPlan.price;
                const sellingPlanName = sellingPlan.name;
                const isTrial = sellingPlan.isTrial;
                const subscriptionType = isTrial ? 'trial' : selectedSubscriptionType;

                const displaySellingPlanName = sellingPlan.shortName || sellingPlanName.toLowerCase();

                this.changePrice(enableSubscription, subscriptionType, sellingPlanPrice, displaySellingPlanName, this.productPrice);
            }
    
            changePrice(enableSubscription, subscriptionType, sellingPlanPrice, sellingPlanName, productPrice) {
                let price = '';
                
                if (!enableSubscription) {
                    price = productPrice ? `${formatMoney(productPrice, window.money_format)}` : '';
                } else if (subscriptionType === 'subscription') {
                    price = productPrice ? `${formatMoney(productPrice, window.money_format)} + ${formatMoney(sellingPlanPrice, window.money_format)} / ${sellingPlanName}` : `${formatMoney(sellingPlanPrice, window.money_format)} / ${sellingPlanName}`;
                } else {
                    price = productPrice ? `${formatMoney(productPrice + sellingPlanPrice, window.money_format)}` : `${formatMoney(sellingPlanPrice, window.money_format)}`;
                }
                
                this.innerText = price;
            }
        }

        customElements.define('subscription-service-price', SubscriptionServicePrice);
    }
})();
